import { FC } from 'react';

import Icon from 'molecules/Icon';

import { FeatureComparisonCellProps } from 'components/Tables/FeatureComparisonTable/config';

interface FeatureTableCellProps extends FeatureComparisonCellProps {
  iconSize?: number;
}

// Icons are SVG-only, so screen readers and LLMs reading the HTML see an empty cell (and shift the
// next cell's text into it). This text is visually hidden and only rendered when there's no visible label.
const iconText: Record<string, string> = { check: 'Yes', 'x-mark': 'No', warning: 'Partial' };

const FeatureTableCell: FC<FeatureTableCellProps> = ({ cell, text, label, iconSize = 25 }) => {
  const renderIcon = () => {
    switch (cell) {
      case 'check':
        return <Icon icon="circle-check-1" size={iconSize} iconColor="text-primary-300" />;
      case 'x-mark':
        return <Icon icon="x-circle" size={iconSize} iconColor="text-gray-500" />;
      case 'warning':
        return <Icon icon="warning" size={iconSize} iconColor="text-gray-500" />;
      default:
        return null;
    }
  };

  if (cell === 'text') {
    return (
      <>
        <div className="flex items-center justify-end sm:hidden">
          <span className="text-right text-xs leading-4 text-gray-300">{text}</span>
        </div>

        <div className="hidden items-center justify-end sm:flex">
          <span className="text-right text-lg font-medium leading-7 text-gray-300">{text}</span>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex min-w-[90px] flex-col items-center justify-center gap-1 sm:hidden">
        <div className="flex justify-center">{renderIcon()}</div>
        {!label && cell && iconText[cell] && <span className="sr-only">{iconText[cell]}</span>}
        {label && <span className="block max-w-[90px] text-center text-xs leading-4 text-gray-300">{label}</span>}
      </div>

      <div className="hidden w-full items-center justify-start gap-2.5 sm:flex">
        <div className="flex justify-center">{renderIcon()}</div>
        {!label && cell && iconText[cell] && <span className="sr-only">{iconText[cell]}</span>}
        {label && <span className="text-lg font-medium leading-7 text-gray-300">{label}</span>}
      </div>
    </>
  );
};

export default FeatureTableCell;
